import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import * as R from 'ramda';
import useSize from '@react-hook/size';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import makeStyles from '@mui/styles/makeStyles';
import { visuallyHidden } from '@mui/utils';
import { Tooltip } from '../';
import { T4_BREAKPOINTS } from '../theme';
import useKeyboardEscapeHandler from '../hooks/useKeybordEscapeHandler';

var HEIGHT = 42;

var useStyles = makeStyles(function (theme) {
  return {
    footer: {
      minHeight: HEIGHT
    },
    legend: {
      flexGrow: 1
    },
    copyright: {
      padding: 0
    },
    source: {
      padding: theme.spacing(0, 1, 0, 1)
    },
    logo: {
      height: HEIGHT / 2,
      display: 'flex'
    },
    sticky: {
      position: 'sticky',
      right: 0
    }
  };
});

var DataFooter = function DataFooter(_ref) {
  var _cx;

  var logo = _ref.logo,
      source = _ref.source,
      copyright = _ref.copyright,
      legend = _ref.legend,
      _ref$isSticky = _ref.isSticky,
      isSticky = _ref$isSticky === undefined ? false : _ref$isSticky;

  var target = React.useRef(null);

  var _useSize = useSize(target),
      width = _useSize[0];

  var isWidthUp = R.gt(width);
  var isSourced = source && source.label && source.link;
  var isCopyrighted = copyright && copyright.label && copyright.content;
  var classes = useStyles();

  var _useKeyboardEscapeHan = useKeyboardEscapeHandler(),
      open = _useKeyboardEscapeHan.open,
      setOpen = _useKeyboardEscapeHan.setOpen;

  var typoSize = isWidthUp(T4_BREAKPOINTS.lg) ? 14 : 12;
  if (!isWidthUp(T4_BREAKPOINTS.xs)) return React.createElement(Grid, { ref: target });

  return React.createElement(
    Grid,
    {
      ref: target,
      container: true,
      className: classes.footer,
      direction: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    React.createElement(
      Grid,
      { item: true, className: classes.legend },
      isWidthUp(T4_BREAKPOINTS.sm) && legend
    ),
    React.createElement(
      Grid,
      { item: true, style: { alignSelf: 'flex-end' }, className: cx((_cx = {}, _cx[classes.sticky] = isSticky, _cx)) },
      React.createElement(
        Grid,
        {
          container: true,
          justifyContent: 'flex-end',
          alignItems: 'center',
          className: classes.footer,
          spacing: 0
        },
        React.createElement(
          Grid,
          {
            item: true,
            className: isWidthUp(T4_BREAKPOINTS.sm) && isSourced ? classes.copyright : classes.source
          },
          isCopyrighted && React.createElement(
            Fragment,
            null,
            React.createElement(
              Tooltip,
              {
                title: React.createElement(
                  Typography,
                  { variant: 'body2' },
                  copyright.content
                ),
                'aria-label': copyright.content,
                variant: 'light',
                tabIndex: 0,
                'aria-hidden': false,
                placement: 'top',
                enterNextDelay: 100,
                arrow: true,
                open: open,
                onOpen: function onOpen() {
                  return setOpen(true);
                },
                onClose: function onClose() {
                  return setOpen(false);
                }
              },
              React.createElement(
                Typography,
                { variant: 'body2', style: { cursor: 'pointer', fontSize: typoSize } },
                copyright.label
              )
            ),
            React.createElement(
              Typography,
              { sx: visuallyHidden },
              copyright.content
            )
          )
        ),
        isWidthUp(T4_BREAKPOINTS.sm) && isSourced && React.createElement(
          Grid,
          { item: true, className: classes.source },
          React.createElement(
            Typography,
            { variant: 'body2', style: { fontSize: typoSize } },
            React.createElement(
              Link,
              { href: source.link, rel: 'noopener noreferrer', target: '_blank', variant: 'body2' },
              source.label
            )
          )
        ),
        isWidthUp(T4_BREAKPOINTS.xs) && logo && React.createElement(
          Grid,
          { item: true },
          React.createElement('img', { src: logo, alt: 'logo', className: classes.logo })
        )
      )
    )
  );
};

DataFooter.propTypes = process.env.NODE_ENV !== "production" ? {
  logo: PropTypes.string,
  source: PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    link: PropTypes.string
  }),
  copyright: PropTypes.shape({
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
  }),
  legend: PropTypes.element,
  isSticky: PropTypes.bool
} : {};

export default DataFooter;